import { Box, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { FC, useMemo } from 'react'
import { formatCurrency } from '../../../utils'
import { calculateBillEntriesSubtotals } from '../../../utils/billing'
import InfoIcon from '@mui/icons-material/Info'
import { flexCenter } from '../../../theme/styleConstants'
import { BillItem } from '../../../interfaceTypes/billing'

interface BillEntriesSubtotalsProps {
  billItems: BillItem[]
  isClientView: boolean | undefined
}

const CustomTable = styled(Table)(({ theme }) => ({
  minWidth: '600px',
  border: '1px solid #E0E0E0',
  '& .MuiTableCell-root': {
    padding: theme.spacing(1),
    fontSize: '10px !important',
    backgroundColor: 'transparent'
  }
}))

const HeaderTypography = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 400,
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1)
}))

const BillEntriesSubtotals: FC<BillEntriesSubtotalsProps> = ({ billItems, isClientView }) => {
  const subtotals = useMemo(() => calculateBillEntriesSubtotals(billItems), [billItems])

  if (!!isClientView) {
    return null
  }

  return (
    <Box
      mt={2}
      sx={{
        '@media print': {
          display: 'none'
        }
      }}
    >
      <HeaderTypography variant="h6">Fee Breakdown</HeaderTypography>

      <CustomTable size="small">
        <TableHead>
          <TableRow>
            <TableCell width="70%">Entry Type</TableCell>
            <TableCell width="15%">Total Hours</TableCell>
            <TableCell width="15%">Total Fees</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {subtotals.map(row => (
            <TableRow key={row.type}>
              <TableCell>{row.type}</TableCell>
              <TableCell>{row.duration.toFixed(2)}</TableCell>
              <TableCell>{formatCurrency(row.total)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </CustomTable>

      <Typography
        sx={{ ...flexCenter, justifyContent: 'flex-start', gap: '5px' }}
        color="text.secondary"
        fontSize="11px"
        mt={1}
      >
        <InfoIcon sx={{ width: '15px', height: '15px' }} color="action" />
        Note: Above breakdown is for internal reference only and will not be visible to the client.
      </Typography>
    </Box>
  )
}

export default BillEntriesSubtotals
